<template>
  <RevViewWrapper
    revType="problemSolving"
    :organizationId="organizationId"
    :revId="revId"
    :mode="mode"
    :rev="rev"
    :resetRev="resetRev"
    :get="get"
    :update="update"
    :translate="translateRev"
    :like="like"
    :unlike="unlike"
    :approve="approve"
    :disapprove="disapprove"
    :complete="complete"
    :goToTab="goToStep"
    :createValidationRequest="createValidationRequest"
    :cancelValidationRequest="cancelRequest"
    :setCoach="setCoach"
    :canCompleteRev="canCompleteRev"
  >
    <div
      class="ma-0 pa-0"
      slot-scope="{
        isEditMode,
        organizationId,
        accountSiteId,
        accountId,
        edit,
        canComplete,
        delete: canDelete,
        isLiked,
        isApproved,
        isTranslating,
        isTranslated,
        translateRev,
        validate,
        iconOptions,
        menuOptions,
        revId,
        errors,
        revTags,
        saveRev,
        setValidationRequest,
        setCancelRequest,
        completeRev,
        likeRev,
        unlikeRev,
        approveRev,
        disapproveRev,
        handleCancelClick,
        setEditMode,
        setViewMode,
        setRevChanged,
        isSaving,
        hasCompleteError,
        implementRev,
        highlightRev,
        removeApproval,
        approve,
        revApprovals,
        isImplementedValidated,
        hasRequestValidation,
        canDoFinancialValidation,
        implement,
        showImplementError,
        plannerIntegration,
        replicateRev,
        downloadPowerpoint,
      }"
    >
      <v-container class="ma-0 pa-0">
        <ChangedStatusAlert
          v-if="hasChangedStatusReason"
          class="mt-1"
          data-testid="problemSolvingView-changedStatusAlert-alert"
          :data="rev"
        />
        <v-row class="my-0 py-0">
          <v-col md="8">
            <v-card elevation="2" rounded>
              <Header
                likeTid="problemSolving-button-like"
                likedTid="problemSolving-button-liked"
                shareType="ProblemSolving"
                revType="problemSolving"
                :isEditMode="isEditMode"
                :organizationId="organizationId"
                :accountSiteId="accountSiteId"
                :accountId="accountId"
                :rev="rev"
                :mediaObject="backgroundHeaderImage"
                :availableOwners="availableOwners"
                :availableCoaches="availableCoaches"
                :availableCollaborators="availableCollaborators"
                :trackStartDelete="trackStartDelete"
                :trackCancelDelete="trackCancelDelete"
                :isLiked="isLiked"
                :isApproved="isApproved"
                :iconOptions="iconOptions"
                :menuOptions="menuOptions"
                :deleteRev="deleteRev"
                :setCoach="setCoach"
                :setOwner="setOwner"
                :setCollaborators="setCollaborators"
                :updateName="updateName"
                :isSaving="isSaving"
                :canSave="canSave"
                :trackUpdateRev="trackUpdateRev"
                :trackPrintRev="trackPrintRev"
                :isTranslating="isTranslating"
                :showStatusModal="showStatusModal"
                :revApprovals="revApprovals"
                :isImplementedValidated="isImplementedValidated"
                :hasRequestValidation="hasRequestValidation"
                :plannerIntegration="plannerIntegration"
                @toggleTranslate="translateRev"
                @editRev="setEditMode"
                @save="saveRev"
                @like="likeRev"
                @unlike="unlikeRev"
                @approve="approveRev"
                @disapprove="disapproveRev"
                @revChanged="setRevChanged"
                @cancel="handleCancelClick"
                @validate="handleValidateClick"
                @statusChanged="
                  $event === $constants.revStatus.completed
                    ? completeRev(canCompleteRev)
                    : handleStatusChanged($event)
                "
                @hideModal="showStatusModal = false"
                @changeModalStatus="handleModalStatusChanged($event)"
                @implement="implementRev"
                @highlight="highlightRev"
                @downloadPowerpoint="downloadPowerpoint"
                @replicateRev="replicateRev(rev)"
              />
            </v-card>
            <PredictedFinancialImpactAlert
              data-testid="problemSolvingView-alert-predictedFinancialImpact"
              :rev="rev"
            />
            <Stepper
              class="my-2"
              :steps="STEPS"
              :phases="phases"
              :activeStep="activeStep"
              :hasCompleteError="hasCompleteError"
              :hasRequestValidation="hasRequestValidation"
              :showImplementError="showImplementError"
              @click="goToStep($event, 'stepper')"
            />
            <v-card class="pa-8">
              <div v-show="activeStep === STEPS.DEFINITION">
                <ProblemDefinition
                  hasNextButton
                  :isTranslating="isTranslating"
                  :isSaving="isSaving"
                  :isEditMode="isEditMode"
                  :rev="rev"
                  :markRequiredFields="hasCompleteError"
                  @revChange="setRevChanged"
                  @next="goToStep(2)"
                />
              </div>
              <div v-show="activeStep === STEPS.MEASURE">
                <Measure
                  hasNextButton
                  hasBackButton
                  :isTranslating="isTranslating"
                  :isSaving="isSaving"
                  :isEditMode="isEditMode"
                  :rev="rev"
                  :markRequiredFields="hasCompleteError"
                  @revChange="setRevChanged"
                  @next="goToStep(3)"
                  @back="goToStep(1)"
                />
              </div>
              <div v-show="activeStep === STEPS.ANALYSIS">
                <ProblemAnalysis
                  hasNextButton
                  hasBackButton
                  :isTranslating="isTranslating"
                  :isEditMode="isEditMode"
                  :rev="rev"
                  :markRequiredFields="hasCompleteError"
                  @revChange="setRevChanged"
                  @next="goToStep(4)"
                  @back="goToStep(2)"
                />
              </div>
              <div v-show="activeStep === STEPS.SOLUTIONS">
                <Solutions
                  hasNextButton
                  hasBackButton
                  :isTranslating="isTranslating"
                  :isSaving="isSaving"
                  :isEditMode="isEditMode"
                  :rev="rev"
                  :markRequiredFields="hasCompleteError"
                  :canComplete="canComplete"
                  @completeRev="completeRev(canCompleteRev)"
                  @revChange="setRevChanged"
                  @goToStep="goToStep"
                  @setEditMode="setEditMode"
                  @setViewMode="setViewMode"
                  @removeCompleted="removeCompleted"
                  @next="goToStep(5)"
                  @back="goToStep(3)"
                />
              </div>
              <div v-show="activeStep === STEPS.REVIEW">
                <Review
                  hasBackButton
                  :isSaving="isSaving"
                  :hasRequestValidation="hasRequestValidation"
                  :isTranslating="isTranslating"
                  :isEditMode="isEditMode"
                  :rev="rev"
                  :canImplement="implement"
                  :canDoFinancialValidation="canDoFinancialValidation"
                  @implementRev="implementRev"
                  @revChange="setRevChanged"
                  @createValidationRequest="setValidationRequest"
                  @cancelValidationRequest="setCancelRequest"
                  @back="goToStep(4)"
                  @validate="handleValidateClick"
                />
              </div>
            </v-card>
          </v-col>
          <v-col md="4">
            <RevSidebar
              submitCommentTestId="problem_solving_comments_submit"
              modelName="problem-solving"
              hasChallengeActive
              hasCommentsActive
              hasAttachmentsActive
              :modelType="$t(`common.${modelTypeName}_one`)"
              :modelId="revId"
              :canEdit="canComplete"
              :isEditMode="isEditMode"
              :rev="rev"
              :errors="errors"
              :tags="revTags"
              @priorityChanged="updatePriority"
              @onDueDateChange="updateDueDate"
              @onNotesChange="updateNotes"
              @revChanged="setRevChanged"
              @locationChange="locationChange"
            />
          </v-col>
        </v-row>
      </v-container>
    </div>
  </RevViewWrapper>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import _ from 'lodash'

import { utils } from 'frontend-js-shared'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin.vue'

import RevViewWrapper from '@/containers/RevViewWrapper/RevViewWrapper.vue'
import Header from '@/components/Rev/Header.vue'
import RevSidebar from '@/components/Rev/RevSidebar.vue'

import ProblemDefinition from '@/containers/ProblemSolvingView/ProblemDefinition.vue'
import ProblemAnalysis from '@/containers/ProblemSolvingView/ProblemAnalysis.vue'
import Measure from '@/containers/ProblemSolvingView/Measure.vue'
import Solutions from '@/containers/ProblemSolvingView/Solutions/Solutions.vue'
import Review from '@/containers/ProblemSolvingView/Review.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import ChangedStatusAlert from '@/components/Core/ChangedStatusAlert.vue'
import Stepper from '@/components/Stepper'
import PredictedFinancialImpactAlert from '@/components/Rev/PredictedFinancialImpactAlert.vue'

const STEPS = {
  DEFINITION: 1,
  MEASURE: 2,
  ANALYSIS: 3,
  SOLUTIONS: 4,
  REVIEW: 5,
}

export default {
  name: 'ProblemSolvingView',

  mixins: [mixin, trackerMixin],

  components: {
    RevViewWrapper,
    Header,
    RevSidebar,
    ProblemDefinition,
    ProblemAnalysis,
    Measure,
    Solutions,
    Review,
    ChangedStatusAlert,
    Stepper,
    PredictedFinancialImpactAlert,
  },

  props: {
    organizationId: {
      type: String,
      required: true,
    },
    revId: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: false,
    },
    initialStep: {
      type: Number,
      required: false,
    },
  },

  data() {
    return {
      STEPS,
      activeStep: Number(this.initialStep ?? this.$route.query?.tabIndex ?? 1),
      canSave: true,
      isLoading: false,
      showValidate: false,
      showStatusModal: false,
    }
  },

  computed: {
    ...mapState('problemSolving', {
      rev: ({ rev }) => rev,
      availableOwners: state => _.get(state, 'availableOwners', []),
      availableCoaches: state => _.get(state, 'availableCoaches', []),
      availableCollaborators: state =>
        _.get(state, 'availableCollaborators', []),
    }),

    backgroundHeaderImage() {
      return _.get(this.rev.problemAnalysis, 'problemMedia[0]', null)
    },

    hasChangedStatusReason() {
      if (this.rev?.status == this.$constants.revStatus.canceled)
        return Boolean(this.rev?.canceledReason)
      if (this.rev?.status == this.$constants.revStatus.onHold)
        return Boolean(this.rev?.onHoldReason)
      return false
    },

    modelTypeName() {
      return _.snakeCase(this.rev.type)
    },

    hasDefinitionMissingFields() {
      const { problemAnalysis = {} } = this.rev
      const { problem, containment, goals } = problemAnalysis
      return !(problem && (containment || goals))
    },

    hasAnalysisMissingFields() {
      const { rootCauses } = this.rev
      const hasAtLeastOneCompleteRootCause = rootCauses.reduce(
        (compliesCriteria, rootCause) => {
          if (compliesCriteria) return true
          return rootCause?.name?.trim().length > 0
        },
        false
      )
      return !hasAtLeastOneCompleteRootCause
    },

    hasSolutionsMissingFields() {
      const { rootCauses, solutionDescription } = this.rev

      const atLeastOneRootCauseWasFilled = rootCauses.some(
        rootCause =>
          rootCause.correctiveAction &&
          !_.isNull(rootCause.correctiveActionWorked) &&
          (rootCause.correctiveLearning || rootCause.preventiveAction)
      )

      if (!atLeastOneRootCauseWasFilled) return true

      return this.atLeastOneCorrectiveActionWorked && !solutionDescription
    },

    atLeastOneCorrectiveActionWorked() {
      return this.rev.rootCauses.some(
        rootCause => rootCause.correctiveActionWorked
      )
    },

    canCompleteRev() {
      return utils.revs.canComplete(this.rev)
    },

    isMeasureComplete() {
      const measure = {
        media: this.rev?.problemAnalysis?.measureMedia ?? [],
        description: this.rev?.problemAnalysis?.measureDescription ?? '',
      }

      return this.isObjectNotEmpty(measure)
    },

    isReviewComplete() {
      return (
        this.isObjectNotEmpty({
          ...this.rev?.review,
          ...this.rev?.reviewPhase,
        }) || Boolean(this.rev?.hasFinancialValidation)
      )
    },

    phases() {
      return [
        {
          name: this.$t('problem_solving.tabs.definition'),
          complete: !this.hasDefinitionMissingFields,
          missingFields: this.hasDefinitionMissingFields,
          id: this.$constants.problemSolving.phases.definition,
        },
        {
          name: this.$t('problem_solving.tabs.measure'),
          complete: this.isMeasureComplete,
          missingFields: false,
          id: this.$constants.problemSolving.phases.measure,
        },
        {
          name: this.$t('problem_solving.tabs.analysis'),
          complete: !this.hasAnalysisMissingFields,
          missingFields: this.hasAnalysisMissingFields,
          id: this.$constants.problemSolving.phases.analysis,
        },
        {
          name: this.$t('problem_solving.tabs.solutions'),
          complete: !this.hasSolutionsMissingFields,
          missingFields: this.hasSolutionsMissingFields,
          id: this.$constants.problemSolving.phases.solutions,
        },
        {
          name: this.$t('rev.validation.validate'),
          complete: this.isReviewComplete,
          missingFields: false,
          id: this.$constants.problemSolving.phases.review,
        },
      ]
    },
  },

  methods: {
    ...mapMutations({
      resetRev: 'problemSolving/resetRev',
      setCoach: 'problemSolving/setCoach',
      setOwner: 'problemSolving/setOwner',
      setCollaborators: 'problemSolving/setCollaborators',
      updateName: 'problemSolving/updateName',
      updateLocation: 'problemSolving/updateLocation',
      updatePriority: 'problemSolving/updatePriority',
      updateDueDate: 'problemSolving/updateDueDate',
      updateNotes: 'problemSolving/updateNotes',
    }),

    ...mapActions({
      get: 'problemSolving/get',
      update: 'problemSolving/update',
      like: 'problemSolving/like',
      unlike: 'problemSolving/unlike',
      approve: 'problemSolving/approveRev',
      disapprove: 'problemSolving/disapproveRev',
      delete: 'problemSolving/delete',
      translate: 'problemSolving/translateRev',
      createValidationRequest: 'problemSolving/validationRequest',
      cancelRequest: 'problemSolving/updateRequestStatus',
    }),

    locationChange(attrs) {
      if (attrs.site) {
        this.canSave = true
        this.updateLocation({
          ...attrs,
          organization: { _id: this.organizationId },
        })
      } else {
        this.canSave = false
      }
    },

    async translateRev() {
      try {
        await this.translate({
          revId: this.revId,
          organizationId: this.organizationId,
          targetLanguage: this.getLocaleWithoutDialect(
            this.$store.getters.language
          ),
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async deleteRev() {
      return this.delete({
        organizationId: this.organizationId,
        revId: this.rev.id,
      })
        .then(() => {
          this.trackDelete(this.rev, 'problemSolving', 'complete', 'success')
        })
        .catch(err => {
          this.trackDelete(this.rev, 'problemSolving', 'complete', 'fail', err)
        })
    },

    complete() {
      return this.updateCurrentRev({ status: 'completed' }).then(
        this.getCurrentRev
      )
    },

    updateStatus(status) {
      this.updateCurrentRev({ status }).then(this.getCurrentRev)
    },

    handleModalStatusChanged(payload) {
      if (payload.status == this.$constants.revStatus.canceled) {
        this.updateCurrentRev({
          canceledReason: payload.reason,
          status: payload.status,
        }).then(this.getCurrentRev)
        this.trackStatusChanged(payload.status, {
          canceledReason: Boolean(payload.reason),
        })
      }

      if (payload.status == this.$constants.revStatus.onHold) {
        this.updateCurrentRev({
          onHoldReason: payload.reason,
          status: payload.status,
        }).then(this.getCurrentRev)
        this.trackStatusChanged(payload.status, {
          onHoldReason: Boolean(payload.reason),
        })
      }
      this.showStatusModal = false
    },

    trackStatusChanged(status, reason) {
      this.trackRevStatusChanged(
        this.rev,
        this.isEditMode,
        'selector',
        status,
        reason
      )
    },

    handleStatusChanged(status) {
      if (
        status === this.$constants.revStatus.canceled ||
        status === this.$constants.revStatus.onHold
      )
        return (this.showStatusModal = true)
      this.updateStatus(status)
      this.trackStatusChanged(status, null)
    },

    removeCompleted() {
      this.updateCurrentRev({ status: 'inProgress' }).then(this.getCurrentRev)
    },

    updateCurrentRev(changes) {
      return this.update({
        organizationId: this.organizationId,
        revId: this.rev.id,
        data: { ...this.rev, ...changes },
      })
    },

    getCurrentRev() {
      return this.get({
        organizationId: this.organizationId,
        revId: this.rev.id,
      })
    },

    goToStep(step, from = 'button') {
      this.trackRevNavigate(this.rev, this.phases[step - 1].id, from)
      if (step === this.activeStep) return
      this.activeStep = step

      this.$router.replace({
        path: this.$route.fullPath,
        query: { ...this.$route.query, tabIndex: step },
      })
    },

    trackCancelDelete() {
      this.trackDelete(this.rev, 'problemSolving', 'cancel')
    },

    trackStartDelete() {
      this.trackDelete(this.rev, 'problemSolving', 'start')
    },

    trackUpdateRev(changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    trackPrintRev() {
      this.trackPrint(this.rev)
    },

    trackNavigateRev(phase) {
      this.trackRevNavigate(this.rev, phase, 'tab')
    },

    handleValidateClick() {
      this.trackClickValidateIcon({ action: 'goToValidationView' })
      return this.navigateTo({
        name: 'validateRev',
        params: {
          revId: this.revId,
          revType: 'problem-solving',
        },
      })
    },
  },
  beforeRouteLeave(to, from, next) {
    eventHub.$emit(HUB_EVENTS.REV_VIEW.BEFORE_ROUTE_LEAVE, { to, from, next })
  },
}
</script>
